// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/styles';
import { get, isEmpty, upperFirst } from 'lodash';
import { bool, func, shape, string } from 'prop-types';
import Grid from '@material-ui/core/Grid';

import {
  DetailsHeader,
  Footer,
  FormActionButtons,
  TabsContainer,
  TabPanel,
} from '../../../components';
import {
  fetchTemplateCategory,
  createTemplateCategory,
  deleteTemplateCategory,
  updateTemplateCategory,
} from "../../../actions/templateCategory";
import {
  getDetails,
  getIsLoading,
  getCreatedCategory,
  getCreateResult,
  getDeleteResult,
  getUpdateResult,
  getUpdateTemplatesResult,
  getValidationErrors,
} from "../../../reducers/templateCategory";
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  FAIL,
  SUCCESS,
} from '../../../constants';
import DetailsContainer from '../../details';
import { getI18n } from '../../../utils/intl';
import Description from './Description';
import Management from './Management';
import Templates from './Templates';
import Apis from './Apis';
import CategoryOrganizationAccess from './OrganizationAccess';
import { getTabIndex } from '../../../utils';

import styles from './styles';

export const TCDetails = (props) => {
  const {
    classes,
    createdCategory,
    createResult,
    createTemplateCategory,
    deleteResult,
    deleteTemplateCategory,
    details = {},
    fetchTemplateCategory,
    isLoading = false,
    updateResult,
    updateTemplateCategory,
    updateTemplatesResult,
    validationErrors,
    history,
  } = props;

  const pageHeaderRef = useRef(null);
  const intl = getI18n(useIntl());
  const [notificationStatus, setNotificationStatus] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isEditingName, setIsEditingName] = useState(true);
  const [categoryFormObject, setCategoryFormObject] = useState({});
  const { uuid } = details;
  const { name: categoryName } = categoryFormObject;
  const categoryUuid = get(props, 'match.params.categoryUuid');
  const [inTemplatesAddMode, setInTemplatesAddMode] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [unsavedDialog, setUnsavedDialog] = useState(false);
  const [isTemplatesTabDirty, setIsTemplatesTabDirty] = useState(false);
  const [isOrgAccessTabDirty, setIsOrgAccessTabDirty] = useState(false);
  const [isTabSwitchInProgress, setIsTabSwitchInProgress] = useState(false);
  const [tabToSwitchAfterConfirm, setTabToSwitchAfterConfirm] = useState();
  const [indexOfTabToSwitchAfterConfirm, setIndexOfTabToSwitchAfterConfirm] = useState();
  const [showNameUniqueError, setShowNameUniqueError] = useState(false);

  const handleTemplatesChange = (isFormDirty) => {
    setIsTemplatesTabDirty(isFormDirty);
  };

  const handleOrgsChange = (isFormDirty) => {
    setIsOrgAccessTabDirty(isFormDirty);
  };

  const saveCategoryDetails = () => {
    if (uuid) {
      const {
        allowNonCompliantApisToBeUpdated,
        defaultCategory,
        enforceTemplateOrdering,
        templateUsageRestriction,
        criteria,
      } = categoryFormObject;
      updateTemplateCategory({
        allowNonCompliantApisToBeUpdated,
        defaultCategory,
        description: categoryFormObject.description,
        enforceTemplateOrdering,
        name: categoryName,
        templateUsageRestriction,
        uuid,
        accessType: categoryFormObject.accessType,
        flexible: categoryFormObject.flexible,
        criteria,
      });
    } else {
      createTemplateCategory({
        name: categoryName,
        description: categoryFormObject.description,
      });
    }
  };

  const CATEGORY_TEMPLATES_TAB = 'templates-tab';
  const CATEGORY_MANAGEMENT_TAB = 'management-tab';
  const CATEGORY_DESCRIPTION_TAB = 'description-tab';
  const CATEGORY_ORGANIZATIONS_TAB = 'orgs-tab';
  const CATEGORY_APIS_TAB = 'apis-tab';

  const getTabItems = () => [
    {
      id: 'overview',
      tabId: CATEGORY_DESCRIPTION_TAB,
      label: intl.getI18nMessage('label.category.details.tab.title.overview'),
      visible: true,
    },
    {
      disabled: !uuid,
      id: 'templates',
      tabId: CATEGORY_TEMPLATES_TAB,
      label: intl.getI18nMessage('label.category.details.tab.title.templates'),
      visible: true,
    },
    {
      disabled: !uuid,
      id: 'management',
      tabId: CATEGORY_MANAGEMENT_TAB,
      label: intl.getI18nMessage('label.category.details.tab.title.management'),
      visible: true,
    },
    {
      disabled: !uuid,
      id: 'apis',
      tabId: CATEGORY_APIS_TAB,
      label: intl.getI18nMessage('label.category.details.tab.title.apis'),
      visible: true,
    },
    {
      id: 'organizations',
      tabId: CATEGORY_ORGANIZATIONS_TAB,
      label: intl.getI18nMessage('label.category.details.tab.title.organizations'),
      visible: true,
      disabled: get(details, 'accessType') !== 'ORG',
    },
  ];

  const [currentTab, setCurrentTab] = useState(CATEGORY_DESCRIPTION_TAB);

  const isFormDirty = useMemo(() => {
    // following conversions needed to handle default value null
    const detailsDescription = details.description || '';
    const formDescription = categoryFormObject.description || '';
    const {
      allowNonCompliantApisToBeUpdated,
      enforceTemplateOrdering,
      templateUsageRestriction,
      criteria,
    } = details;
    const {
      allowNonCompliantApisToBeUpdated: newAllowNonCompliantApisToBeUpdate,
      enforceTemplateOrdering: newEnforceTemplateOrdering,
      templateUsageRestriction: newTemplateUsage,
      criteria: newCriteria,
    } = categoryFormObject;
    if (uuid) {
      return (details.name !== categoryName ||
        detailsDescription !== formDescription ||
        allowNonCompliantApisToBeUpdated !== newAllowNonCompliantApisToBeUpdate ||
        enforceTemplateOrdering !== newEnforceTemplateOrdering ||
        templateUsageRestriction !== newTemplateUsage ||
        get(criteria, 'apiType') !== get(newCriteria, 'apiType') ||
        get(criteria, 'apiAccess') !== get(newCriteria, 'apiAccess') ||
        get(details, 'accessType') !== get(categoryFormObject, 'accessType') ||
        get(details, 'flexible') !== get(categoryFormObject, 'flexible')
      );
    }
    return !!categoryName;
  }, [categoryFormObject, categoryName, details]);

  const handleTabChange = (event, newValue) => {
    const tabs = getTabItems();
    const newTabId = tabs[newValue].tabId;
    if ((isFormDirty && (currentTab !== CATEGORY_TEMPLATES_TAB)) ||
      (isTemplatesTabDirty && (currentTab === CATEGORY_TEMPLATES_TAB)) ||
      (isOrgAccessTabDirty && (currentTab === CATEGORY_ORGANIZATIONS_TAB))
    ) {
      setIsTabSwitchInProgress(true);
      setUnsavedDialog(true);
      setTabToSwitchAfterConfirm(newTabId);
      setIndexOfTabToSwitchAfterConfirm(newValue);
    } else {
      setTabIndex(newValue);
      setCurrentTab(newTabId);
      history.push(`/admin/console/templatecategories/details/${uuid}/${tabs[newValue].id}`);
    }
  };

  useEffect(() => {
    const tabs = getTabItems();
    const val = getTabIndex(getTabItems(), props.match)
    const newTabId = tabs[val].tabId;
    setCurrentTab(newTabId);
    setTabIndex(val);
  }, [props.match]);

  const onCancel = () => {
    if (uuid) {
      fetchTemplateCategory({ uuid });
    } else {
      window.location.href = '/admin/console/templatecategories';
    }
  };

  useEffect(()=> {
    if ((currentTab !== CATEGORY_TEMPLATES_TAB) && categoryUuid) {
      fetchTemplateCategory({ uuid: categoryUuid });
    }
  },[currentTab, props.match]);

  useEffect(() => {
    if (createResult === SUCCESS) {
      const { uuid } = createdCategory;
      setShowNameUniqueError(false);
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage("label.category.details.create.success"));
      setTimeout(() => {
        notifyMessages('', '');
        window.location.href = `/admin/console/templatecategories/details/${uuid}`;
      }, 2500);
    } else if (createResult === FAIL) {
      const firstValidationErrorObj = get(validationErrors, '[0]', '');
      if (firstValidationErrorObj.key === 'error.validation.field.notunique') {
        setShowNameUniqueError(true);
      }
      if(firstValidationErrorObj) {
        notifyMessages(ALERT_ERROR,
          `${upperFirst(firstValidationErrorObj.field)}: ${upperFirst(firstValidationErrorObj.error)}`);
      } else {
        notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.category.details.create.fail'));
      }
    }
  }, [createResult]);

  useEffect(() => {
    if (deleteResult === SUCCESS) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.category.details.delete.success'));
      setTimeout(() => {
        notifyMessages('', '');
        window.location.href = '/admin/console/templatecategories';
      }, 2500);
    } else if (deleteResult === FAIL) {
      if(validationErrors && validationErrors[0] && validationErrors[0].error) {
        notifyMessages(ALERT_ERROR, validationErrors[0].error);
      } else {
        notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.category.details.delete.fail'));
      }
    }
  }, [deleteResult]);

  useEffect(() => {
    if (!isEmpty(details)) {
      setCategoryFormObject(details);
      setIsEditingName(false);
    }
  }, [details]);

  useEffect(() => {
    if (updateTemplatesResult === SUCCESS) {
      if (inTemplatesAddMode) {
        setInTemplatesAddMode(false);
      }
      fetchTemplateCategory({ uuid: categoryUuid });
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.category.details.update.success'));
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    } else if (updateTemplatesResult === FAIL) {
      if(validationErrors && validationErrors[0] && validationErrors[0].error) {
        notifyMessages(ALERT_ERROR, validationErrors[0].error);
      } else {
        notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.category.details.update.fail'));
      }
    }
  }, [updateTemplatesResult]);

  useEffect(() => {
    if (updateResult === SUCCESS) {
      fetchTemplateCategory({ uuid: categoryUuid });
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.category.details.update.success'));
      setTimeout(() => {
        notifyMessages('', '');
      }, 2500);
    } else if (updateResult === FAIL) {
      if(validationErrors && validationErrors[0] && validationErrors[0].error) {
        notifyMessages(ALERT_ERROR, validationErrors[0].error);
      } else {
        notifyMessages(ALERT_ERROR, intl.getI18nMessage('label.category.details.update.fail'));
      }
    }
  }, [updateResult]);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  const onDescriptionChange = (val) => {
    setCategoryFormObject({
      ...categoryFormObject,
      description: val,
    });
  };

  const onNameChange = (val) => {
    setCategoryFormObject({
      ...categoryFormObject,
      name: val,
    });
  };

  const onDelete = () => {
    deleteTemplateCategory(uuid);
  };

  const onDialogClose = () => {
    setUnsavedDialog(false);
    if (isTabSwitchInProgress) {
      setIsTabSwitchInProgress(false);
      setTabToSwitchAfterConfirm();
      setIndexOfTabToSwitchAfterConfirm();
    }
  };

  const onDialogSubmit = () => {
    setUnsavedDialog(false);
    if (isTabSwitchInProgress) {
      if (isTabSwitchInProgress) {
        setIsTabSwitchInProgress(false);
        setTabToSwitchAfterConfirm();
        setIndexOfTabToSwitchAfterConfirm();
        setTabIndex(indexOfTabToSwitchAfterConfirm);
        setCurrentTab(tabToSwitchAfterConfirm);
      }
    } else {
      if (uuid) {
        fetchTemplateCategory({ uuid });
      } else {
        window.location.href = '/admin/console/templatecategories';
      }
    }
  };

  const showFormActionsButton = currentTab !== CATEGORY_TEMPLATES_TAB &&
                       currentTab !== CATEGORY_ORGANIZATIONS_TAB;

  return (
    <DetailsContainer
      detailsPageId="category-details-page"
      isLoading={isLoading}
      notificationId="category-details-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      notificationContainerClass={classes.notificationContainer}
      hasNotificationIcon={true}
      isActionsEnabled={false}
      pageHeaderRef={pageHeaderRef}
      isDialogOpen={unsavedDialog}
      onDialogClose={onDialogClose}
      onDialogSubmit={onDialogSubmit}
      dialogSubmitText={intl.getI18nMessage('label.dialog.unsaved.changes.submit')}
      header={
        <DetailsHeader
          deleteButtonLabel={intl.getI18nMessage('label.details.header.delete')}
          deleteFn={onDelete}
          handleChange={onNameChange}
          headerId="category-details-header-container"
          helperText={intl.getI18nMessage('label.category.name.help')}
          isFieldEdit={isEditingName || !categoryName}
          isFieldEditIcon={currentTab === CATEGORY_DESCRIPTION_TAB}
          isNameError={showNameUniqueError}
          marker={''}
          name={categoryName}
          onClickEditIcon={() => { setIsEditingName(true); }}
          placeHolderText={'Template Name'}
          showDelete={categoryUuid}
          status={''}
          title={''}
          textFieldClass={classes.nameFieldClass}
          type="textField"
        />
      }
      tabs={
        <TabsContainer
          handleTabChange={handleTabChange}
          id={'category-details-tab-container'}
          orientation="horizontal"
          tabItems={getTabItems()}
          tabValue={tabIndex}
        />
      }
      page={
        (<>
          <Grid>
            <TabPanel visible={currentTab === CATEGORY_TEMPLATES_TAB}>
              <Templates
                categoryUuid={uuid}
                currentTab={currentTab}
                handleTemplatesChange={handleTemplatesChange}
                inTemplatesAddMode={inTemplatesAddMode}
                setInTemplatesAddMode={setInTemplatesAddMode}
              />
            </TabPanel>
            <TabPanel visible={currentTab === CATEGORY_MANAGEMENT_TAB}>
              <Management
                currentTab={currentTab}
                formObject={categoryFormObject}
                updateFormObject={setCategoryFormObject}
              />
            </TabPanel>
            <TabPanel visible={currentTab === CATEGORY_DESCRIPTION_TAB}>
              <Description
                currentTab={currentTab}
                description={categoryFormObject.description}
                onDescriptionChange={onDescriptionChange}
              />
            </TabPanel>
            <TabPanel visible={currentTab === CATEGORY_APIS_TAB}>
              <Apis
                categoryUuid={uuid}
                currentTab={currentTab}
              />
            </TabPanel>
            <TabPanel visible={currentTab === CATEGORY_ORGANIZATIONS_TAB}>
              <CategoryOrganizationAccess
                categoryUuid={uuid}
                currentTab={currentTab}
                handleOrgsChange={handleOrgsChange}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
          </Grid>
          <Footer />
        </>)
      }
      footer={
        (showFormActionsButton && isFormDirty) ? <FormActionButtons
          assistText={''}
          isContainerFooter
          isDisabled={false}
          nextText={intl.getI18nMessage(`label.user.details.button.${categoryUuid ? 'save': 'create'}`)}
          onNextClick={saveCategoryDetails}
          onCancelClick={onCancel}
          id="category-form-buttons"
        /> : null
      }
    />
  );
};

const mapStateToProps = state => ({
  createdCategory: getCreatedCategory(state),
  createResult: getCreateResult(state),
  deleteResult: getDeleteResult(state),
  details: getDetails(state),
  isLoading: getIsLoading(state),
  updateResult: getUpdateResult(state),
  updateTemplatesResult: getUpdateTemplatesResult(state),
  validationErrors: getValidationErrors(state),
});

const mapDispatchToProps = {
  createTemplateCategory,
  deleteTemplateCategory,
  fetchTemplateCategory,
  updateTemplateCategory,
};

TCDetails.propTypes = {
  classes: shape({}),
  createdCategory: shape({}),
  createResult: string,
  createTemplateCategory: func,
  deleteResult: string,
  deleteTemplateCategory: func,
  details: shape({}),
  fetchTemplateCategory: func,
  isLoading: bool,
  updateResult: string,
  updateTemplateCategory: func,
  updateTemplatesResult: string,
  validationErrors: shape({}),
  history: shape({}),
  match: shape({}),
};

TCDetails.displayName = 'TemplateCategoryDetails';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(TCDetails);
